import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { AuthProvider } from "./contexts/AuthContext";
import { RefreshProvider } from "./contexts/RefreshContext";
import AppNavigator from "./AppNavigator";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import CredentialsDialog from "./components/CredentialsDialog";
import AnnouncementDialog from "./components/AnnouncementDialog";
import useFcmToken from "./hooks/useFcmToken";
import useLocationPermission from "./hooks/useLocationPermission";

// Mounted inside AuthProvider so the FCM permission prompt fires on every
// route — including the login screen — and the device token is POSTed as soon
// as the user authenticates.
function FcmBootstrap() {
  useFcmToken();
  return null;
}

// Location permission is requested at launch too — geofenced login needs
// coordinates before the user is even signed in.
function LocationBootstrap() {
  useLocationPermission();
  return null;
}

function App() {
  // Listen for service-worker messages so background notifications can play a sound
  // and (optionally) navigate the foreground tab.
  useEffect(() => {
    const handleSwMessage = (event) => {
      const data = event.data || {};
      if (data.type === "PLAY_NOTIFICATION_SOUND") {
        try {
          const audio = new Audio("/notification-sound.wav");
          audio.volume = 0.7;
          audio.play().catch(() => {});
        } catch {
          /* noop */
        }
      }
      if (data.type === "NAVIGATE" && typeof data.url === "string") {
        window.location.href = data.url;
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSwMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
      <RefreshProvider>
        <AuthProvider>
          <FcmBootstrap />
          <LocationBootstrap />
          <AppNavigator />
          {/* Inside AuthProvider — the popup reads the signed-in user's role. */}
          <AnnouncementDialog />
        </AuthProvider>
      </RefreshProvider>
      <PwaInstallPrompt />
      <CredentialsDialog />
    </>
  );
}

export default App;
